/**
 * Вспомогательные утилиты общего назначения
 */

import { SupportedLanguage } from '@/types/config.types';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Форматирует timestamp в читаемое время
 * @param timestamp - временная метка в миллисекундах
 * @param locale - локаль для форматирования
 * @returns строка времени (например "14:32")
 */
export function formatTime(timestamp: number, locale: string = 'ru-RU'): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Форматирует timestamp в читаемую дату
 * @param timestamp - временная метка в миллисекундах
 * @param locale - локаль для форматирования
 * @returns строка даты (например "8 октября 2025")
 */
export function formatDate(timestamp: number, locale: string = 'ru-RU'): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Debounce функция
 * @param func - функция для debounce
 * @param wait - время ожидания в мс
 * @returns debounced функция
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle функция
 * @param func - функция для throttle
 * @param limit - лимит времени в мс
 * @returns throttled функция
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Определяет язык браузера/системы
 * @returns код языка (ru, en, de, fr) или 'ru' по умолчанию
 */
export function detectBrowserLanguage(): SupportedLanguage {
  // Пытаемся получить язык из HTML тега
  const htmlLang = document.documentElement.lang;
  if (htmlLang) {
    const lang = htmlLang.toLowerCase().split('-')[0];
    if (isSupportedLanguage(lang)) {
      return lang;
    }
  }

  // Пытаемся получить из navigator
  const navLang = navigator.language || (navigator as unknown as { userLanguage?: string }).userLanguage;
  if (navLang) {
    const lang = navLang.toLowerCase().split('-')[0];
    if (isSupportedLanguage(lang)) {
      return lang;
    }
  }

  // Дефолт — английский (международный сайт)
  return 'en';
}

/**
 * Проверяет, поддерживается ли язык
 * @param lang - код языка
 * @returns true если язык поддерживается
 */
export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return ['ru', 'en', 'de', 'fr'].includes(lang);
}

/**
 * Проверяет, является ли устройство мобильным
 * @returns true если мобильное устройство
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Проверяет, является ли устройство iOS
 * @returns true если iOS
 */
export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * Добавляет класс к body для отключения скролла
 */
export function disableBodyScroll(): void {
  document.body.style.overflow = 'hidden';
  
  // Для iOS
  if (isIOS()) {
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
  }
}

/**
 * Убирает класс и включает скролл
 */
export function enableBodyScroll(): void {
  document.body.style.overflow = '';
  
  // Для iOS
  if (isIOS()) {
    document.body.style.position = '';
    document.body.style.width = '';
  }
}

/**
 * Копирует текст в буфер обмена
 * @param text - текст для копирования
 * @returns Promise<boolean> успешно ли скопировано
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback для старых браузеров
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  } catch (error) {
    chatLogger.error('[Clipboard] Ошибка копирования:', error);
    return false;
  }
}

/**
 * Создает задержку (для async/await)
 * @param ms - миллисекунды
 * @returns Promise
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Экранирует HTML для безопасного отображения
 * @param text - текст для экранирования
 * @returns экранированный текст
 */
export function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };

  return text.replace(/[&<>"']/g, (char) => map[char] || char);
}

